import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  Timestamp
} from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { getDb, getStorage } from '../firebase';

export const uploadDocument = async (familyId, file, metadata, onProgress) => {
  const db = getDb();
  const storage = getStorage();
  const docRef = doc(collection(db, 'families', familyId, 'documents'));
  const storagePath = `families/${familyId}/documents/${docRef.id}/${file.name}`;
  const storageRef = ref(storage, storagePath);

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        if (onProgress) onProgress(progress);
      },
      (error) => reject(error),
      async () => {
        try {
          const storageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          const data = {
            name: metadata.name,
            category: metadata.category,
            childIds: metadata.childIds || [],
            uploadedBy: metadata.uploadedBy,
            uploadedByName: metadata.uploadedByName,
            storageUrl,
            storagePath,
            fileType: file.type,
            fileSize: file.size,
            originalFileName: file.name,
            createdAt: Timestamp.now()
          };
          await setDoc(docRef, data);
          resolve({ id: docRef.id, ...data });
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

/**
 * Create a Documents-vault record for a file that has already been uploaded to
 * Storage (e.g. via uploadAttachment). Lets messages/activities file an
 * attachment into the vault without a second upload, and avoids the blob-has-no
 * -.name issue that uploadDocument's path has on React Native.
 *
 * @param {string} familyId
 * @param {{ name, category, childIds?, uploadedBy, uploadedByName, storageUrl, storagePath, fileType?, fileSize?, originalFileName? }} data
 * @returns {Promise<{ id, ...record }>}
 */
export const createDocumentRecord = async (familyId, data) => {
  const db = getDb();
  const docRef = doc(collection(db, 'families', familyId, 'documents'));
  const record = {
    name: data.name,
    category: data.category,
    childIds: data.childIds || [],
    uploadedBy: data.uploadedBy,
    uploadedByName: data.uploadedByName,
    storageUrl: data.storageUrl,
    storagePath: data.storagePath,
    fileType: data.fileType || '',
    fileSize: data.fileSize != null ? data.fileSize : null,
    originalFileName: data.originalFileName || data.name,
    createdAt: Timestamp.now(),
  };
  await setDoc(docRef, record);
  return { id: docRef.id, ...record };
};

export const deleteDocument = async (familyId, docId, storagePath) => {
  const db = getDb();
  const storage = getStorage();
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.warn('Storage file already deleted or not found:', error);
  }
  await deleteDoc(doc(db, 'families', familyId, 'documents', docId));
};

export const subscribeToDocuments = (familyId, callback) => {
  const db = getDb();
  const q = query(
    collection(db, 'families', familyId, 'documents')
  );
  return onSnapshot(q, (snapshot) => {
    const documents = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(documents);
  }, (error) => {
    console.error('Documents subscription error:', error);
  });
};
